// src/products/userInvites/pages/EmailTemplatesPage.js
// Purpose: Preview system email templates - shows what invitation emails will look like

import React, { useState } from 'react';
import { 
  Mail, 
  Eye,
  Info,
  FileText
} from 'lucide-react';
import { useGlobalStore } from '../../../state/globalStore';

const EmailTemplatesPage = ({ user }) => {
  const { userProfile } = useGlobalStore();
  const [selectedProduct, setSelectedProduct] = useState('dataroom');

  // System templates - these are the actual templates used when sending emails
  const systemTemplates = [
    {
      id: 'default',
      name: 'Default Invitation',
      product: 'all',
      subject: 'You have been invited to Kapitalized',
      description: 'Used for general invitations and products without specific templates'
    },
    {
      id: 'dataroom',
      name: 'Dataroom Invitation',
      product: 'dataroom',
      subject: 'You\'re Invited! - Access Dataroom on Kapitalized',
      description: 'Used when inviting users to dataroom projects'
    },
    {
      id: 'capraise',
      name: 'CapRaise Invitation',
      product: 'capraise',
      subject: 'Investment Opportunity - You\'re Invited to Review on Kapitalized',
      description: 'Used when inviting users to capital raising rounds'
    },
    {
      id: 'equity',
      name: 'Equity Invitation',
      product: 'equity',
      subject: 'You have been invited to Kapitalized',
      description: 'Used when inviting users to equity management'
    },
    {
      id: 'questions',
      name: 'Questions Invitation',
      product: 'questions',
      subject: 'You have been invited to Kapitalized',
      description: 'Used when inviting users to Q&A modules'
    }
  ];

  const products = [
    { id: 'dataroom', name: 'Dataroom', color: 'bg-blue-500' },
    { id: 'capraise', name: 'CapRaise', color: 'bg-green-500' },
    { id: 'equity', name: 'Equity', color: 'bg-purple-500' },
    { id: 'questions', name: 'Questions', color: 'bg-orange-500' }
  ];

  // Sample data for preview
  const previewData = {
    magic_link_url: 'https://kapitalized.com/accept-invite?token=abc123xyz',
    current_year: new Date().getFullYear(),
    inviter_name: (userProfile?.first_name && userProfile?.last_name)
      ? `${userProfile.first_name} ${userProfile.last_name}`
      : userProfile?.first_name || userProfile?.last_name || user?.user_metadata?.first_name || user?.user_metadata?.last_name || 'John Doe',
    product_name: products.find(p => p.id === selectedProduct)?.name || 'Kapitalized',
    custom_message: 'Welcome to our platform! We\'re excited to have you join us.'
  };

  // Get template HTML based on product
  const getTemplateHtml = (product) => {
    const templates = {
      dataroom: `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Dataroom Invitation - Kapitalized</title>
    <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; background-color: #f4f4f4; margin: 0; padding: 0; }
        .container { max-width: 600px; margin: 20px auto; background-color: #ffffff; border-radius: 8px; box-shadow: 0 0 10px rgba(0, 0, 0, 0.1); overflow: hidden; }
        .header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 30px 20px; text-align: center; }
        .header h1 { margin: 0 0 10px 0; font-size: 28px; font-weight: 600; }
        .header h2 { margin: 0; font-size: 18px; font-weight: 400; opacity: 0.9; }
        .content { background: white; padding: 30px 20px; }
        .button-container { text-align: center; margin: 30px 0; }
        .button { display: inline-block; background: #667eea; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: 600; font-size: 16px; }
        .link-fallback { word-break: break-all; color: #667eea; font-size: 14px; margin-top: 20px; }
        .footer { background: #f7f9fc; padding: 20px; text-align: center; font-size: 14px; color: #666; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>You're Invited!</h1>
            <h2>Access Dataroom on Kapitalized</h2>
        </div>
        <div class="content">
            <p>Hello,</p>
            <p>You've been invited to access a secure dataroom on Kapitalized. Click the button below to get started:</p>
            <div class="button-container">
                <a href="{{magic_link_url}}" class="button">Access Dataroom</a>
            </div>
            <p>If the button doesn't work, copy and paste this link into your browser:</p>
            <p class="link-fallback">{{magic_link_url}}</p>
            <p>If you weren't expecting this invitation, you can safely ignore this email.</p>
        </div>
        <div class="footer">
            <p>This invitation was sent via Kapitalized</p>
            <p>&copy; {{current_year}} Kapitalized. All rights reserved.</p>
        </div>
    </div>
</body>
</html>`,
      capraise: `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Investment Opportunity - Kapitalized</title>
    <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; background-color: #f4f4f4; margin: 0; padding: 0; }
        .container { max-width: 600px; margin: 20px auto; background-color: #ffffff; border-radius: 8px; box-shadow: 0 0 10px rgba(0, 0, 0, 0.1); overflow: hidden; }
        .header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 30px 20px; text-align: center; }
        .header h1 { margin: 0 0 10px 0; font-size: 28px; font-weight: 600; }
        .header h2 { margin: 0; font-size: 18px; font-weight: 400; opacity: 0.9; }
        .content { background: white; padding: 30px 20px; }
        .button-container { text-align: center; margin: 30px 0; }
        .button { display: inline-block; background: #667eea; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: 600; font-size: 16px; }
        .link-fallback { word-break: break-all; color: #667eea; font-size: 14px; margin-top: 20px; }
        .footer { background: #f7f9fc; padding: 20px; text-align: center; font-size: 14px; color: #666; }
        .confidential { background-color: #fff3cd; border: 1px solid #ffeaa7; padding: 12px; border-radius: 4px; margin: 20px 0; font-size: 14px; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>Investment Opportunity</h1>
            <h2>You're Invited to Review</h2>
        </div>
        <div class="content">
            <p>Hello,</p>
            <p>You've been invited to review an exclusive investment opportunity on Kapitalized. Click the button below to access the details:</p>
            <div class="button-container">
                <a href="{{magic_link_url}}" class="button">Review Investment</a>
            </div>
            <p>If the button doesn't work, copy and paste this link into your browser:</p>
            <p class="link-fallback">{{magic_link_url}}</p>
            <div class="confidential">
                <strong>Confidential:</strong> This invitation is confidential and intended solely for you.
            </div>
        </div>
        <div class="footer">
            <p>This invitation was sent via Kapitalized</p>
            <p>&copy; {{current_year}} Kapitalized. All rights reserved.</p>
        </div>
    </div>
</body>
</html>`,
      default: `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Welcome to Kapitalized</title>
    <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; background-color: #f4f4f4; margin: 0; padding: 0; }
        .container { max-width: 600px; margin: 20px auto; background-color: #ffffff; border-radius: 8px; box-shadow: 0 0 10px rgba(0, 0, 0, 0.1); overflow: hidden; }
        .header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 30px 20px; text-align: center; }
        .header h1 { margin: 0 0 10px 0; font-size: 28px; font-weight: 600; }
        .header h2 { margin: 0; font-size: 18px; font-weight: 400; opacity: 0.9; }
        .content { background: white; padding: 30px 20px; }
        .button-container { text-align: center; margin: 30px 0; }
        .button { display: inline-block; background: #667eea; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: 600; font-size: 16px; }
        .link-fallback { word-break: break-all; color: #667eea; font-size: 14px; margin-top: 20px; }
        .footer { background: #f7f9fc; padding: 20px; text-align: center; font-size: 14px; color: #666; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>You're Invited!</h1>
            <h2>Join Kapitalized</h2>
        </div>
        <div class="content">
            <p>Hello,</p>
            <p>You've been invited to join Kapitalized. Click the button below to get started:</p>
            <div class="button-container">
                <a href="{{magic_link_url}}" class="button">Get Started</a>
            </div>
            <p>If the button doesn't work, copy and paste this link into your browser:</p>
            <p class="link-fallback">{{magic_link_url}}</p>
        </div>
        <div class="footer">
            <p>This invitation was sent via Kapitalized</p>
            <p>&copy; {{current_year}} Kapitalized. All rights reserved.</p>
        </div>
    </div>
</body>
</html>`
    };

    // Return product-specific template or default
    return templates[product] || templates.default;
  };

  // Process template with variables
  const processTemplate = (html, data = previewData) => {
    if (!html) return '';
    
    let processed = html;
    
    // Replace variables
    Object.keys(data).forEach(key => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      processed = processed.replace(regex, data[key] || '');
    });

    return processed;
  };

  // Get template for selected product
  const getTemplateForProduct = (product) => {
    const template = systemTemplates.find(t => t.product === product) || systemTemplates.find(t => t.product === 'all');
    return template;
  };

  const selectedTemplate = getTemplateForProduct(selectedProduct);
  const templateHtml = getTemplateHtml(selectedProduct === 'dataroom' ? 'dataroom' : selectedProduct === 'capraise' ? 'capraise' : 'default');
  const processedHtml = processTemplate(templateHtml);

  return (
    <div className="p-6">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <Mail className="w-8 h-8 text-blue-600 mr-3" />
          <h1 className="text-3xl font-bold text-gray-900">Email Preview</h1>
        </div>
        <p className="text-gray-600">
          Preview what invitation emails will look like when sent. Templates are managed by system administrators.
        </p>
      </div>

      {/* Info Banner */}
      <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <Info className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">System Templates</p>
            <p>These are the system email templates that will be used when sending invitations. Templates are managed in <strong>AdminHQ → Email Templates</strong> by system administrators.</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        
        {/* Product Selector */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Select Product</h2>
              <p className="text-sm text-gray-600 mt-1">Choose a product to preview its email template</p>
            </div>
            
            <div className="p-4">
              <div className="space-y-2">
                {products.map((product) => {
                  const isSelected = selectedProduct === product.id;
                  
                  return (
                    <button
                      key={product.id}
                      onClick={() => setSelectedProduct(product.id)}
                      className={`w-full p-3 rounded-lg text-left transition-all border ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full mr-3 ${product.color}`}></div>
                        <span className={`font-medium ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                          {product.name}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Email Preview */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Email Preview</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedTemplate?.description || 'Preview of invitation email'}
                  </p>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Eye className="w-4 h-4 mr-1" />
                  Preview Only
                </div>
              </div>
            </div>

            <div className="p-4">
              {/* Email Subject */}
              <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="text-sm font-medium text-gray-700 mb-1">Email Subject:</div>
                <div className="text-sm text-gray-900">{selectedTemplate?.subject || 'You have been invited to Kapitalized'}</div>
              </div>

              {/* Email From */}
              <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="text-sm font-medium text-gray-700 mb-1">From:</div>
                <div className="text-sm text-gray-900">
                  {/* This will use the global From Name from AdminHQ settings */}
                  Kapitalized &lt;noreply@kapitalized.com&gt;
                </div>
                <p className="text-xs text-gray-500 mt-1">From name is configured in AdminHQ → User Invites</p>
              </div>

              {/* Email Body Preview */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
                  <span className="text-sm text-gray-600">Email Body Preview</span>
                  <span className="text-xs text-gray-500">Sample data used for preview</span>
                </div>
                <div 
                  className="max-h-[600px] overflow-y-auto bg-white"
                  dangerouslySetInnerHTML={{ __html: processedHtml }}
                />
              </div>

              {/* Template Info */}
              <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Template Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Template:</span>
                    <span className="ml-2 text-gray-600">{selectedTemplate?.name || 'Default'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Product:</span>
                    <span className="ml-2 text-gray-600">
                      {products.find(p => p.id === selectedProduct)?.name || selectedProduct}
                    </span>
                  </div>
                  <div className="md:col-span-2">
                    <span className="font-medium text-gray-700">Location:</span>
                    <span className="ml-2 text-gray-600 font-mono text-xs">
                      api/email_templates/user_invite_{selectedProduct === 'dataroom' ? 'dataroom' : selectedProduct === 'capraise' ? 'capraise' : 'default'}.html
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Variables Reference */}
      <div className="mt-8 bg-blue-50 rounded-lg p-6 border border-blue-200">
        <h3 className="font-medium text-blue-900 mb-3 flex items-center">
          <FileText className="w-5 h-5 mr-2" />
          Available Variables in Templates
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
          <div>
            <p><code className="bg-blue-100 px-1 rounded">{'{{magic_link_url}}'}</code> - The invitation acceptance link</p>
            <p><code className="bg-blue-100 px-1 rounded">{'{{product_name}}'}</code> - Name of the product (Dataroom, CapRaise, etc.)</p>
            <p><code className="bg-blue-100 px-1 rounded">{'{{inviter_name}}'}</code> - Name of the person sending the invite</p>
          </div>
          <div>
            <p><code className="bg-blue-100 px-1 rounded">{'{{custom_message}}'}</code> - Personal message (if provided by inviter)</p>
            <p><code className="bg-blue-100 px-1 rounded">{'{{current_year}}'}</code> - Current year</p>
          </div>
        </div>
        <p className="text-xs text-blue-700 mt-3">
          These variables are automatically replaced with actual values when emails are sent.
        </p>
      </div>
    </div>
  );
};

export default EmailTemplatesPage;
